import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { BaseSubsectionAdapter } from 'components/Assessments/adapters/BaseSubsectionAdapter'
import { AdapterOptions, FlatAnswerMutation } from 'components/Assessments/adapters/types'
import { getAnswerValuesFromItems } from 'components/Assessments/helpers'
import { FuaFormData } from 'components/Assessments/FuaAssessment/types/FuaFormData'
import { VitalsFormField } from './formField'
import { VitalsQuestionnaireItem } from './questionnaire'

/**
 * Vitals subsection adapter for FUA Assessment
 * Handles vitals extraction and transformation
 */
export class VitalsAdapter extends BaseSubsectionAdapter<FuaFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<FuaFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    const temperature = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_TEMPERATURE
    )?.at(0)
    const pulse = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_PULSE
    )?.at(0)
    const respirations = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_RESPIRATIONS
    )?.at(0)
    const o2Saturation = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_O2_SATURATION
    )?.at(0)
    const bloodPressure = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_BLOOD_PRESSURE
    )?.at(0)
    const height = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_HEIGHT
    )?.at(0)
    const bloodSugar = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_BLOOD_SUGAR
    )?.at(0)
    const weight = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_WEIGHT
    )?.at(0)
    const initialsAuthorization = getAnswerValuesFromItems(
      flatAnswersArray,
      VitalsQuestionnaireItem.VITALS_INITIALS_AUTHORIZATION
    )?.at(0)

    return {
      [VitalsFormField.VITALS_TEMPERATURE]: temperature,
      [VitalsFormField.VITALS_PULSE]: pulse,
      [VitalsFormField.VITALS_RESPIRATIONS]: respirations,
      [VitalsFormField.VITALS_O2_SATURATION]: o2Saturation,
      [VitalsFormField.VITALS_BLOOD_PRESSURE]: bloodPressure,
      [VitalsFormField.VITALS_HEIGHT]: height,
      [VitalsFormField.VITALS_BLOOD_SUGAR]: bloodSugar,
      [VitalsFormField.VITALS_WEIGHT]: weight,
      [VitalsFormField.VITALS_INITIALS_AUTHORIZATION]: initialsAuthorization
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(VitalsFormField.VITALS_TEMPERATURE)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_TEMPERATURE,
        value: this.getFormDataField(VitalsFormField.VITALS_TEMPERATURE)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_PULSE)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_PULSE,
        value: this.getFormDataField(VitalsFormField.VITALS_PULSE)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_RESPIRATIONS)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_RESPIRATIONS,
        value: this.getFormDataField(VitalsFormField.VITALS_RESPIRATIONS)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_O2_SATURATION)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_O2_SATURATION,
        value: this.getFormDataField(VitalsFormField.VITALS_O2_SATURATION)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_BLOOD_PRESSURE)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_BLOOD_PRESSURE,
        value: this.getFormDataField(VitalsFormField.VITALS_BLOOD_PRESSURE)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_HEIGHT)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_HEIGHT,
        value: this.getFormDataField(VitalsFormField.VITALS_HEIGHT)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_BLOOD_SUGAR)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_BLOOD_SUGAR,
        value: this.getFormDataField(VitalsFormField.VITALS_BLOOD_SUGAR)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_WEIGHT)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_WEIGHT,
        value: this.getFormDataField(VitalsFormField.VITALS_WEIGHT)
      })
    }

    if (this.formDataFieldExists(VitalsFormField.VITALS_INITIALS_AUTHORIZATION)) {
      mutations.push({
        linkId: VitalsQuestionnaireItem.VITALS_INITIALS_AUTHORIZATION,
        value: this.getFormDataField(VitalsFormField.VITALS_INITIALS_AUTHORIZATION)
      })
    }

    return mutations
  }
}
